import React, { Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { CContainer, CSpinner } from '@coreui/react-pro'
import './scss/style.scss'
import { useDispatch, useSelector } from 'react-redux'
import { authActions } from './store/auth.slice'
import lazyWithRetry from './utils/lazyWithRetry'
import ChunkErrorBoundary from './components/ChunkErrorBoundary'
import VersionWatcher from './components/VersionWatcher'
import { currentNext, rememberBrandForUser, safeNext, sessionLoginPath } from './brand/brandStorage'
import PinnedBrandGuard, { SignedInBrandedLogin } from './brand/PinnedBrandGuard'
import BrandedPage from './components/branded-page/BrandedPage'

// Containers
const DefaultLayout = lazyWithRetry(() => import('./layout/DefaultLayout'))
const Login = lazyWithRetry(() => import('./views/public/login/Login'))
const LoginEntry = lazyWithRetry(() => import('./views/auth/LoginEntry'))
const RedirectToLogin = lazyWithRetry(() =>
  import('./views/auth/LoginEntry').then((m) => ({ default: m.RedirectToLogin })),
)
const SignUp = lazyWithRetry(() => import('./views/public/signup/SignUp'))

// Public self-signup is disabled by default. The API endpoints behind it
// (/auth/register, /auth/verify-phone-number) are gated by ENABLE_PUBLIC_SIGNUP and
// return 404, so the page could not complete a signup anyway. Set
// REACT_APP_ENABLE_PUBLIC_SIGNUP=true to re-expose the route alongside the API flag.
const PUBLIC_SIGNUP_ENABLED = process.env.REACT_APP_ENABLE_PUBLIC_SIGNUP === 'true'
const Logout = lazyWithRetry(() => import('./views/public/logout/Logout'))
const ForgotPassword = lazyWithRetry(() => import('./views/public/forgot/ForgotPassword'))
const ResetPassword = lazyWithRetry(() => import('./views/public/reset/ResetPassword'))
const ChangePassword = lazyWithRetry(() => import('./views/public/change/ChangePassword'))
const MfaVerify = lazyWithRetry(() => import('./views/public/mfa-verify/MfaVerify'))
const SsoCallback = lazyWithRetry(() => import('./views/public/sso-callback/SsoCallback'))
const SsoLinkComplete = lazyWithRetry(
  () => import('./views/public/sso-link-complete/SsoLinkComplete'),
)
const SsoError = lazyWithRetry(() => import('./views/public/sso-error/SsoError'))
const VerifyEmail = lazyWithRetry(() => import('./views/public/verify-email/VerifyEmail'))
const Voucher = lazyWithRetry(() => import('./views/public/voucher/Voucher'))
const VoucherLookup = lazyWithRetry(() => import('./views/public/voucher-lookup/VoucherLookup'))
const VoucherWallet = lazyWithRetry(() => import('./views/public/voucher-wallet/VoucherWallet'))
const WalletHandoffLanding = lazyWithRetry(
  () => import('./views/public/wallet-handoff/WalletHandoffLanding'),
)
const Consent = lazyWithRetry(() => import('./views/public/consent/Consent'))
const Unsubscribe = lazyWithRetry(() => import('./views/public/unsubscribe/Unsubscribe'))
const Terms = lazyWithRetry(() => import('./views/public/terms/Terms'))
const Privacy = lazyWithRetry(() => import('./views/public/privacy/Privacy'))
const PrintVoucher = lazyWithRetry(() => import('./views/common/print/PrintVoucher'))

// How long the stored-session check may take before we give up on it and carry
// on with the cached auth. The first request after an idle period is the slow
// one, and that is precisely when this runs.
const SESSION_VALIDATION_TIMEOUT_MS = 8000
// Last resort against a spinner that never clears. It MUST be longer than the
// validation bound above: if it fires first it clears pendingAuth while Redux
// auth is still null, which renders the login page over a perfectly valid
// session — the "I log in, get bounced back to login, and only the second
// attempt works" bug.
const SAFETY_TIMEOUT_MS = 15000

const App = () => {
  const [loading, setLoading] = useState(true)
  const [pendingAuth, setPendingAuth] = useState(null)
  const auth = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const safetyTimeoutRef = React.useRef(null)
  // Set by a fresh sign-in that is about to reload the page (updateAuth(..., redirect=true)).
  // Until that reload lands, the spinner stays up: rendering the signed-in routes at the sign-in
  // URL would hit `/login` -> `/logout` and revoke the session that was just created.
  const redirectingRef = React.useRef(false)

  // Wait for Redux state to actually update before stopping loading
  useEffect(() => {
    if (pendingAuth && auth) {
      console.log('[App] Redux auth state updated, checking if it matches pending auth')
      // Check if the Redux state now has valid auth data
      // More lenient check: just verify auth has user and tokens
      const authIsValid = auth.user && auth.tokens

      if (authIsValid) {
        console.log('[App] Auth state synchronized, setting loading to false')
        // Clear safety timeout since auth loaded successfully
        if (safetyTimeoutRef.current) {
          clearTimeout(safetyTimeoutRef.current)
          safetyTimeoutRef.current = null
        }
        setPendingAuth(null)
        if (redirectingRef.current) return
        setLoading(false)
      } else {
        console.log('[App] Auth state not synchronized yet, waiting...', {
          hasUser: !!auth.user,
          hasTokens: !!auth.tokens,
        })
      }
    }
  }, [auth, pendingAuth])

  const authenticate = (_auth) => {
    console.log('[App.authenticate] Starting authentication', {
      hasAuth: !!_auth,
      hasTenants: _auth?.tenants?.length,
      tenant: _auth?.tenant,
    })
    //if (!_auth) return null
    setLoading(true)
    setPendingAuth(_auth)
    console.log('[App.authenticate] Dispatching auth to Redux, will wait for state sync')
    dispatch(authActions.set(_auth))
    if (_auth) return true
    return false
  }

  // `next` is where to go after a fresh sign-in (validated: same-origin relative path only).
  const updateAuth = (_auth, redirect, next) => {
    console.log('[App.updateAuth] Called with auth:', {
      hasAuth: !!_auth,
      tenantsLength: _auth?.tenants?.length,
      userType: _auth?.user?.type,
      companyTenantsLength: _auth?.user?.company?.tenants?.length,
    })
    //if (!_auth) return null
    if (!_auth) {
      console.log('[App.updateAuth] No auth provided, setting loading to false')
      setLoading(false)
      return
    }

    // Ensure tenants is always an array
    _auth.tenants = Array.isArray(_auth.tenants) ? _auth.tenants : []
    console.log('[App.updateAuth] Tenants after array check:', _auth.tenants)

    if (!_auth.tenants.length && _auth?.user?.type && _auth?.user?.company?.tenants?.length) {
      console.log('[App.updateAuth] No tenants in auth, extracting from company')
      // preselect all tenants - filter out any invalid tenants
      const companyTenants = Array.isArray(_auth.user.company.tenants)
        ? _auth.user.company.tenants
        : []
      const validTenants = companyTenants.filter((t) => t && t.id && t.name && t.code)
      console.log('[App.updateAuth] Valid company tenants:', validTenants)
      _auth.tenants = validTenants.map(({ id, name, code }) => ({
        id,
        name,
        code,
      }))
    }
    _auth.tenant = _auth.tenant ?? _auth.tenants[0]?.id
    _auth.categories = _auth.categories?.results ? _auth.categories?.results : _auth.categories
    console.log('[App.updateAuth] Final auth before authenticate:', {
      tenantsLength: _auth.tenants?.length,
      tenant: _auth.tenant,
      categoriesLength: _auth.categories?.length,
    })
    if (redirect) redirectingRef.current = true
    authenticate(_auth)
    if (redirect) {
      // Remember the brand of whoever just signed in, so the next sign-out or session expiry
      // on this device lands on their airline's sign-in page (Vamos staff: the Vamos page).
      rememberBrandForUser(_auth.user)
      document.location = safeNext(next) || '/'
    }
  }

  // Validate a stored auth blob against the backend before rendering the
  // authenticated layout. Returns 'expired' | 'valid' | 'unknown' (network error).
  // Keeps the loading spinner up during this round-trip so the menu/sidebar
  // never flashes when the session has actually expired (e.g. next-day reload).
  const validateStoredSession = async (storedAuth) => {
    if (!storedAuth?.tokens?.access?.token) return 'expired'
    // Bound the round-trip so this ALWAYS settles. The first request after an
    // idle period is the slow one (cold API, reconnecting DB pool) — and that is
    // exactly when this runs, because the user has just logged back in. Without a
    // bound the safety timeout below could fire first and drop a valid session
    // back to the login page.
    const controller = new AbortController()
    const abortTimer = setTimeout(() => controller.abort(), SESSION_VALIDATION_TIMEOUT_MS)
    try {
      const res = await fetch(`${process.env.REACT_APP_URL_API}/auth/session-status`, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedAuth.tokens.access.token}`,
        },
      })
      if (res.status === 401) return 'expired'
      if (!res.ok) return 'unknown'
      const body = await res.json().catch(() => null)
      if (body?.message === 'Please authenticate') return 'expired'
      if (body?.message?.includes?.('Session expired due to')) {
        localStorage.setItem('session_expired_message', body.message)
        return 'expired'
      }
      return 'valid'
    } catch (error) {
      // Includes the abort above. 'unknown' proceeds with the cached auth, which
      // is the right call: a slow or unreachable check is not evidence that the
      // session is invalid, and the per-request 401 handlers still catch a real
      // expiry on the next API call.
      console.debug('[App] Session validation did not complete, falling back:', error)
      return 'unknown'
    } finally {
      clearTimeout(abortTimer)
    }
  }

  useEffect(() => {
    // Safety timeout: ensure loading never stays true indefinitely
    // Store in ref so it can be cleared when auth syncs
    safetyTimeoutRef.current = setTimeout(() => {
      console.warn('[App] Safety timeout triggered - forcing loading to false')
      setLoading(false)
      setPendingAuth(null)
    }, SAFETY_TIMEOUT_MS)

    console.log('[App] Initial load useEffect running')

    // get stored login
    let _auth = null
    try {
      _auth = localStorage.getItem('auth')
      if (_auth) _auth = JSON.parse(_auth)
    } catch (exception) {
      console.error('Failed to parse auth from localStorage:', exception)
    }
    console.log('[App] Saved auth from localStorage:', {
      hasAuth: !!_auth,
      tenantsLength: _auth?.tenants?.length,
    })

    // check if logged in
    if (!_auth) {
      // no auth
      console.log('[App] No auth found, setting loading to false')
      //authenticate(null)
      clearTimeout(safetyTimeoutRef.current)
      safetyTimeoutRef.current = null
      setLoading(false)
    } else {
      // Validate session with backend before rendering authenticated layout.
      // Keeps the spinner visible during the round-trip — avoids flashing the
      // sidebar/menu when the stored token is already expired.
      console.log('[App] Auth found, validating session with backend before rendering')
      validateStoredSession(_auth).then((result) => {
        if (result === 'expired') {
          console.log('[App] Stored session is expired, clearing auth and routing to login')
          // Land on the expired user's own airline sign-in page, then come back here.
          const signInTarget = sessionLoginPath(
            { notice: 'session_expired', next: currentNext() },
            _auth,
          )
          localStorage.setItem('auth', JSON.stringify(null))
          window.history.replaceState(null, '', signInTarget)
          clearTimeout(safetyTimeoutRef.current)
          safetyTimeoutRef.current = null
          setLoading(false)
        } else {
          // 'valid' or 'unknown' (network down) — proceed with cached auth.
          // Existing per-request 401 handlers will catch real expiries later.
          console.log('[App] Session validation result:', result, '— hydrating auth')
          updateAuth(_auth)
          // Don't clear timeout here - let the auth sync useEffect handle it
        }
      })
    }

    return () => {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current)
        safetyTimeoutRef.current = null
      }
    }
  }, [])

  if (loading)
    return (
      <CContainer className="vh-100 d-flex flex-column justify-content-center align-items-center">
        <CSpinner variant="grow" color="primary" />
      </CContainer>
    )

  if (!auth)
    return (
      <BrowserRouter>
        <VersionWatcher />
        <ChunkErrorBoundary>
          <Suspense
            fallback={
              <CContainer className="vh-100 d-flex flex-column justify-content-center align-items-center">
                <CSpinner variant="grow" color="primary" />
              </CContainer>
            }
          >
            <PinnedBrandGuard />
            <Routes>
              {/* /, /login: Vamos sign-in, or back to the airline remembered on this device */}
              <Route path="/" name="Login" element={<LoginEntry updateAuth={updateAuth} />} />
              <Route path="/login" name="Login" element={<LoginEntry updateAuth={updateAuth} />} />
              {/* Airline-branded staff sign-in: the airline code is the last path segment */}
              <Route path="/login/:code" name="Login" element={<Login updateAuth={updateAuth} />} />
              {PUBLIC_SIGNUP_ENABLED && (
                <Route exact path="/signup" name="Sign Up" element={<SignUp />} />
              )}
              <Route path="/forgot-password" name="Forgot Password" element={<ForgotPassword />} />
              <Route
                path="/forgot-password/:code"
                name="Forgot Password"
                element={<ForgotPassword />}
              />
              <Route
                path="/reset-password/:token"
                name="Reset Password"
                element={<ResetPassword />}
              />
              <Route
                path="/reset-password/:token/:code"
                name="Reset Password"
                element={<ResetPassword />}
              />
              <Route path="/change" name="Change Password" element={<ChangePassword />} />
              <Route
                path="/mfa-verify"
                name="MFA Verification"
                element={<MfaVerify updateAuth={updateAuth} />}
              />
              <Route
                path="/mfa-verify/:code"
                name="MFA Verification"
                element={<MfaVerify updateAuth={updateAuth} />}
              />
              <Route
                path="/sso-callback"
                name="SSO Callback"
                element={<SsoCallback updateAuth={updateAuth} />}
              />
              <Route
                path="/sso-callback/:code"
                name="SSO Callback"
                element={<SsoCallback updateAuth={updateAuth} />}
              />
              <Route
                path="/sso/link/complete"
                name="SSO Link Complete"
                element={<SsoLinkComplete />}
              />
              <Route
                path="/sso/link/complete/:code"
                name="SSO Link Complete"
                element={<SsoLinkComplete />}
              />
              <Route path="/sso/error" name="SSO Error" element={<SsoError />} />
              <Route path="/sso/error/:code" name="SSO Error" element={<SsoError />} />
              <Route
                path="/verify-email/:token"
                name="Verify Email"
                element={<VerifyEmail onLoading={setLoading} />}
              />
              <Route
                path="/voucher/:voucherCode"
                name="Voucher"
                element={
                  <BrandedPage>
                    <Voucher />
                  </BrandedPage>
                }
              />
              <Route
                path="/v/:voucherCode"
                name="Voucher Short"
                element={
                  <BrandedPage>
                    <Voucher />
                  </BrandedPage>
                }
              />
              <Route
                path="/search/:airlineCode"
                name="Voucher Lookup"
                element={
                  <BrandedPage>
                    <VoucherLookup />
                  </BrandedPage>
                }
              />
              <Route
                path="/s/:airlineCode"
                name="Voucher Lookup Short"
                element={
                  <BrandedPage>
                    <VoucherLookup />
                  </BrandedPage>
                }
              />
              <Route
                path="/voucher-wallet/:voucherCode"
                name="Voucher Wallet"
                element={
                  <BrandedPage>
                    <VoucherWallet />
                  </BrandedPage>
                }
              />
              <Route
                path="/vw/:voucherCode"
                name="Voucher Wallet Short"
                element={
                  <BrandedPage>
                    <VoucherWallet />
                  </BrandedPage>
                }
              />
              <Route
                path="/wallet-handoff/:token"
                name="Wallet Handoff"
                element={
                  <BrandedPage>
                    <WalletHandoffLanding />
                  </BrandedPage>
                }
              />
              <Route
                path="/consent/:token"
                name="Consent"
                element={
                  <BrandedPage>
                    <Consent />
                  </BrandedPage>
                }
              />
              <Route
                path="/c/:token"
                name="Consent Short"
                element={
                  <BrandedPage>
                    <Consent />
                  </BrandedPage>
                }
              />
              <Route
                path="/unsubscribe/:token"
                name="Unsubscribe"
                element={
                  <BrandedPage>
                    <Unsubscribe />
                  </BrandedPage>
                }
              />
              <Route
                path="/u/:token"
                name="Unsubscribe Short"
                element={
                  <BrandedPage>
                    <Unsubscribe />
                  </BrandedPage>
                }
              />
              <Route exact path="/terms" name="Terms of Service" element={<Terms />} />
              <Route exact path="/privacy" name="Privacy Policy" element={<Privacy />} />
              <Route path="/terms/:code" name="Terms of Service" element={<Terms />} />
              <Route path="/privacy/:code" name="Privacy Policy" element={<Privacy />} />
              {/* Signed out on an app page: sign in, then come back here */}
              <Route path="*" element={<RedirectToLogin />} />
            </Routes>
          </Suspense>
        </ChunkErrorBoundary>
      </BrowserRouter>
    )

  console.log('isAuth')

  // PCI DSS SAQ A - Check if user must change password
  // SSO logins skip password change - password management is delegated to the identity provider
  const mustChangePassword =
    !auth?.sso_login && (auth?.must_change_password || auth?.user?.must_change_password)

  // If user must change password, show only force-change-password page
  if (mustChangePassword) {
    return (
      <BrowserRouter>
        <VersionWatcher />
        <ChunkErrorBoundary>
          <Suspense
            fallback={
              <CContainer className="vh-100 d-flex flex-column justify-content-center align-items-center">
                <CSpinner variant="grow" color="primary" />
              </CContainer>
            }
          >
            <PinnedBrandGuard auth={auth} />
            <Routes>
              <Route exact path="/logout" element={<Logout authenticate={authenticate} />} />
              <Route path="/change" name="Change Password" element={<ChangePassword />} />
              <Route path="*" element={<Navigate to="/change" />} />
            </Routes>
          </Suspense>
        </ChunkErrorBoundary>
      </BrowserRouter>
    )
  }

  return (
    <BrowserRouter>
      <VersionWatcher />
      <ChunkErrorBoundary>
        <Suspense
          fallback={
            <CContainer className="vh-100 d-flex flex-column justify-content-center align-items-center">
              <CSpinner variant="grow" color="primary" />
            </CContainer>
          }
        >
          <PinnedBrandGuard auth={auth} />
          <Routes>
            <Route exact path="/logout" element={<Logout authenticate={authenticate} />} />
            {/* Redirect /login to logout when authenticated */}
            <Route exact path="/login" element={<Navigate to="/logout" />} />
            {/* Branded sign-in while already signed in (e.g. the installed app's start page):
                the dashboard when the session is that airline's, otherwise sign out and show
                that airline's sign-in page (a branded page is for its own airline only) */}
            <Route path="/login/:code" element={<SignedInBrandedLogin auth={auth} />} />
            {/* Public routes that use public layout even when logged in */}
            <Route
              path="/sso-callback"
              name="SSO Callback"
              element={<SsoCallback updateAuth={updateAuth} />}
            />
            <Route
              path="/sso-callback/:code"
              name="SSO Callback"
              element={<SsoCallback updateAuth={updateAuth} />}
            />
            <Route
              path="/sso/link/complete"
              name="SSO Link Complete"
              element={<SsoLinkComplete />}
            />
            <Route
              path="/sso/link/complete/:code"
              name="SSO Link Complete"
              element={<SsoLinkComplete />}
            />
            <Route path="/sso/error" name="SSO Error" element={<SsoError />} />
            <Route path="/sso/error/:code" name="SSO Error" element={<SsoError />} />
            <Route
              path="/verify-email/:token"
              name="Verify Email"
              element={<VerifyEmail onLoading={setLoading} />}
            />
            <Route
              path="/voucher/:voucherCode"
              name="Voucher"
              element={
                <BrandedPage>
                  <Voucher />
                </BrandedPage>
              }
            />
            <Route
              path="/v/:voucherCode"
              name="Voucher Short"
              element={
                <BrandedPage>
                  <Voucher />
                </BrandedPage>
              }
            />
            <Route
              path="/search/:airlineCode"
              name="Voucher Lookup"
              element={
                <BrandedPage>
                  <VoucherLookup />
                </BrandedPage>
              }
            />
            <Route
              path="/s/:airlineCode"
              name="Voucher Lookup Short"
              element={
                <BrandedPage>
                  <VoucherLookup />
                </BrandedPage>
              }
            />
            <Route
              path="/voucher-wallet/:voucherCode"
              name="Voucher Wallet"
              element={
                <BrandedPage>
                  <VoucherWallet />
                </BrandedPage>
              }
            />
            <Route
              path="/vw/:voucherCode"
              name="Voucher Wallet Short"
              element={
                <BrandedPage>
                  <VoucherWallet />
                </BrandedPage>
              }
            />
            <Route
              path="/wallet-handoff/:token"
              name="Wallet Handoff"
              element={
                <BrandedPage>
                  <WalletHandoffLanding />
                </BrandedPage>
              }
            />
            <Route
              path="/consent/:token"
              name="Consent"
              element={
                <BrandedPage>
                  <Consent />
                </BrandedPage>
              }
            />
            <Route
              path="/c/:token"
              name="Consent Short"
              element={
                <BrandedPage>
                  <Consent />
                </BrandedPage>
              }
            />
            <Route
              path="/unsubscribe/:token"
              name="Unsubscribe"
              element={
                <BrandedPage>
                  <Unsubscribe />
                </BrandedPage>
              }
            />
            <Route
              path="/u/:token"
              name="Unsubscribe Short"
              element={
                <BrandedPage>
                  <Unsubscribe />
                </BrandedPage>
              }
            />
            <Route exact path="/terms" name="Terms of Service" element={<Terms />} />
            <Route exact path="/privacy" name="Privacy Policy" element={<Privacy />} />
            <Route path="/terms/:code" name="Terms of Service" element={<Terms />} />
            <Route path="/privacy/:code" name="Privacy Policy" element={<Privacy />} />
            <Route
              path="/print/:code"
              name="Print Voucher"
              element={
                auth ? (
                  <BrandedPage>
                    <PrintVoucher auth={auth} />
                  </BrandedPage>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="*"
              element={
                auth ? (
                  <DefaultLayout authenticate={authenticate} auth={auth} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
    </BrowserRouter>
  )
}

export default App
